import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { ErrorMessage } from '../components/ErrorMessage';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtDateTime } from './format';
import { attemptsText, nextCallReason, placeText, scopeText, taskNote } from './nextCall';

// NextCallCard: „Nächster Anruf“ – wer als Nächstes angerufen wird und warum (dieselbe Reihenfolge wie „Nächster
// Lead“, ohne Reservierung). Auf der Startseite führt der Knopf in den Arbeitsplatz, dort startet er direkt.
export function NextCallCard({ onStart, starting = false }: { onStart?: () => void; starting?: boolean }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const peek = useQuery({
    queryKey: projectKeys.queuePeek(project.id),
    queryFn: ({ signal }) => papi.queuePeek(undefined, signal),
    refetchInterval: 60_000,
  });
  const [first, ...rest] = peek.data?.items ?? [];
  const startButton = onStart ? (
    <button type="button" className="btn btn-primary" disabled={starting} onClick={onStart} autoFocus>
      Weiter zum Anruf <kbd>N</kbd>
    </button>
  ) : (
    <Link className="btn btn-primary" to={`/p/${project.key}/akquise?start=1`}>
      Weiter zum Anruf
    </Link>
  );

  return (
    <section className="card next-task next-call" aria-labelledby="next-call-title">
      <p className="eyebrow">Nächster Anruf</p>
      {peek.isPending ? (
        <p className="muted">Wird ermittelt …</p>
      ) : peek.isError ? (
        <ErrorMessage error={peek.error} onRetry={() => void peek.refetch()} />
      ) : !peek.data.in_call_hours ? (
        <>
          <h2 id="next-call-title">Außerhalb der Anrufzeiten</h2>
          <p>Weiter geht es {fmtDateTime(peek.data.next_window_at)}.</p>
          {first && (
            <p className="muted">
              Dann als Erstes: {first.name}
              {placeText(first) ? ` (${placeText(first)})` : ''} – {nextCallReason(first)}
            </p>
          )}
        </>
      ) : !first ? (
        <>
          <h2 id="next-call-title">Alles erledigt</h2>
          <p className="muted">Gerade ist kein Lead zum Anrufen fällig. Neue Rückrufe und Wiedervorlagen erscheinen hier automatisch.</p>
        </>
      ) : (
        <>
          <h2 id="next-call-title">{first.name}</h2>
          {placeText(first) && <p className="next-call-place">{placeText(first)}</p>}
          <p className="next-call-reason">
            <strong>Warum jetzt:</strong> {nextCallReason(first)}
          </p>
          {taskNote(first.task_title) && <p className="next-call-task">„{taskNote(first.task_title)}“</p>}
          <p className="muted">{attemptsText(first)}</p>
          <div className="form-actions">{startButton}</div>
          {rest.length > 0 && (
            <p className="next-call-then muted">
              Danach: {rest.map((r) => `${r.name}${r.city ? ` (${r.city})` : ''}`).join(' · ')}
            </p>
          )}
        </>
      )}
      {peek.data?.scope && (
        <p className="next-call-scope">
          <strong>Sie telefonieren für:</strong> {scopeText(peek.data.scope)}
        </p>
      )}
      <p className="next-call-order muted">Reihenfolge: vereinbarte Rückrufe → fällige Wiedervorlagen → neue Leads in Gebieten mit Makler → übrige Leads.</p>
    </section>
  );
}

// NextCallHint: kurze Vorschau im Arbeitsplatz nach dem Speichern („Als Nächstes: …“).
export function NextCallHint({ excludeLeadId }: { excludeLeadId?: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const peek = useQuery({
    queryKey: projectKeys.queuePeek(project.id, excludeLeadId),
    queryFn: ({ signal }) => papi.queuePeek(excludeLeadId, signal),
  });
  const first = peek.data?.items[0];
  if (!peek.data) return null;
  if (!peek.data.in_call_hours) return <p className="muted next-call-hint">Als Nächstes: Anrufzeiten sind vorbei – weiter {fmtDateTime(peek.data.next_window_at)}.</p>;
  if (!first) return <p className="muted next-call-hint">Als Nächstes: nichts fällig – alles erledigt.</p>;
  return (
    <p className="muted next-call-hint">
      Als Nächstes: <strong>{first.name}</strong>
      {placeText(first) ? ` (${placeText(first)})` : ''} – {nextCallReason(first)}
    </p>
  );
}
